import type { ClaimFolder } from "@/api/generated/client/models/ClaimFolder";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Check, FolderPlus } from "lucide-react";

interface FolderPickerProps {
    /**
     * Full list of folders the user can see. Used to resolve the label/color of
     * the currently assigned folder, even when that folder is not assignable
     * (e.g. a member viewing a claim filed into a folder shared via claim_access).
     */
    folders: ClaimFolder[];
    /**
     * Subset of folders the user is allowed to assign claims into. Defaults to
     * `folders` when omitted. Members can only assign into folders they own, so
     * passing the manageable subset prevents offering options the backend will
     * reject with INVALID_CLAIM_FOLDER_ID.
     */
    assignableFolders?: ClaimFolder[];
    value: number | null | undefined;
    onChange: (folderId: number | null) => void;
    disabled?: boolean;
}

/**
 * Compact folder assignment control rendered as a subtle tag/chip button
 * (rather than a full Select) so it reads differently from the Status dropdown.
 */
export function FolderPicker({ folders, assignableFolders, value, onChange, disabled = false }: FolderPickerProps) {
    const current = value ? folders.find((f) => f.id === value) : undefined;
    const options = assignableFolders ?? folders;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    className={cn(
                        "h-8 px-2 text-xs font-normal",
                        disabled && "opacity-50 cursor-not-allowed pointer-events-none",
                        current
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground"
                    )}
                    title={current ? `Folder: ${current.name}` : "Add to folder"}
                >
                    {current ? (
                        <span className="inline-flex items-center gap-1.5">
                            <span
                                className="h-2.5 w-2.5 rounded-full border"
                                style={{ backgroundColor: current.color ?? "#94A3B8" }}
                            />
                            <span className="truncate max-w-[100px]">{current.name}</span>
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5">
                            <FolderPlus className="h-3.5 w-3.5" />
                            <span>Folder</span>
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                    className="text-xs"
                    disabled={disabled}
                    onSelect={() => onChange(null)}
                >
                    <span className="text-muted-foreground">No folder</span>
                    {!current && <Check className="ml-auto h-3.5 w-3.5" />}
                </DropdownMenuItem>
                {options.map((folder) => (
                    <DropdownMenuItem
                        key={folder.id}
                        className="text-xs"
                        disabled={disabled}
                        onSelect={() => onChange(folder.id)}
                    >
                        <span className="inline-flex items-center gap-2">
                            <span
                                className="h-2.5 w-2.5 rounded-full border"
                                style={{ backgroundColor: folder.color ?? "#94A3B8" }}
                            />
                            <span className="truncate">{folder.name}</span>
                        </span>
                        {current?.id === folder.id && <Check className="ml-auto h-3.5 w-3.5" />}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
